import { isEntityScoped } from "@/store/entityTenantStore";
import { useRequestScope } from "@/hooks/useRequestScope";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import type {
  CreateSuperAdminPayload,
  CreateUserPayload,
  TenantExclusionItem,
  UpdateSuperAdminPayload,
  UpdateUserPayload,
} from "@/api/user-management/userManagement.mutations.types";
import type {
  UserDetailAssignedRole,
  UserDetailTemporaryRole,
  UserDetailTenantExclusion,
} from "@/api/user-management/userManagement.queries.types";
import { emailInUseClient } from "@/api/user-management/emailInUse";
import { DetailsStepSection } from "@/components/features/user-management/add-user/DetailsStepSection";
import { EntityAccessStepSection } from "@/components/features/user-management/add-user/EntityAccessStepSection";
import { NewUserEntityAccessStep } from "@/components/features/user-management/add-user/NewUserEntityAccessStep";
import {
  nothingFailed,
  type FollowUpFailures,
  type NamedTemporaryRole,
} from "@/api/user-management/createUserWithAccess";
import {
  planNewUserWrites,
  planServEngineRoles,
  SUPER_ADMIN_ROLE_ID,
} from "@/components/features/user-management/add-user/newUserScope";
import {
  CHOOSE_A_ROLE,
  CHOOSE_AN_END_DATE,
  ROLE_PICKED_TWICE,
  CREATE_FAILED_MESSAGE,
  CREATE_FAILED_TITLE,
  CREATE_REFUSED_MESSAGE,
  CREATE_REFUSED_TITLE,
  CREATE_USER,
  CREATING,
  createdToast,
  DONE,
  EMAIL_IN_USE,
  noEntityCopyMessage,
  partlySavedMessage,
  partlySavedTitle,
  ROLE_COPIES_UNREAD,
  roleInEntity,
  ROLES_UNREAD_TITLE,
  rolesUnreadMessage,
  TRY_AGAIN,
} from "@/components/features/user-management/add-user/newUserCopy";
import styles from "@/components/features/user-management/add-user/add-user.module.scss";
import { Button as BrandButton } from "@/components/ui/Button/Button";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import Header from "@/components/ui/Header/Header";
import Loader from "@/components/ui/Loader/Loader";
import { useActiveSession } from "@/hooks/useActiveSession";
import { SUPER_ADMIN } from "@/components/data/constants";
import { handleApiSuccess, handleApiError } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import {
  useCreateSuperAdmin,
  useCreateUser,
  useRetryNewUserFollowUps,
  useUpdateSuperAdmin,
  useUpdateUser,
} from "@/hooks/user-management/useUserManagementMutations";
import {
  useEntityAccessOptions,
  useUserById,
} from "@/hooks/user-management/useUserManagementQueries";
import { useNewUserScope, useServEngineRoleCopies } from "@/hooks/user-management/useNewUserScope";
import { type DropDownOption } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { PERSON_NAME_REGEX, PERSON_NAME_REGEX_MESSAGE } from "@/constants/inputSanitizers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Group, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Alert } from "@onengine/component-library";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/user-management/add-user/")({
  component: AddUserPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const usPhoneRegex = /^\d{3}-\d{3}-\d{4}$/;
const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;

const phoneNumberSchema = z
  .string()
  .optional()
  .transform((value) => (value ?? "").trim())
  .superRefine((value, ctx) => {
    if (!value) return;
    if (!usPhoneRegex.test(value)) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter phone number in this format (eg:111-222-3333)",
      });
    }
  });

const temporaryRoleSchema = z
  .object({
    roleIds: z.array(z.string()),
    expiresAt: z.union([z.date(), z.string(), z.null()]).transform((val) => {
      if (val === null) return null;
      if (val instanceof Date) return val;
      return new Date(val);
    }),
  })
  .superRefine((data, ctx) => {
    if (data.roleIds.length > 0 && data.expiresAt === null) {
      ctx.addIssue({
        code: "custom",
        message: "Expiration date is required",
        path: ["expiresAt"],
      });
    }
  })
  .transform(({ roleIds, expiresAt }) => ({
    roleIds,
    expiresAt: expiresAt as Date | null,
  }));

const newTemporaryRoleSchema = z
  .object({ roleId: z.string(), endsOn: z.string().nullable() })
  .superRefine((row, ctx) => {
    if (!row.roleId) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_ROLE, path: ["roleId"] });
    } else if (!row.endsOn) {
      ctx.addIssue({ code: "custom", message: CHOOSE_AN_END_DATE, path: ["endsOn"] });
    }
  });

// The access route refuses a second live copy of a temporary role, after the person exists.
const newTemporaryRolesSchema = z.array(newTemporaryRoleSchema).superRefine((rows, ctx) => {
  rows.forEach((row, index) => {
    if (row.roleId && rows.findIndex((other) => other.roleId === row.roleId) < index) {
      ctx.addIssue({ code: "custom", message: ROLE_PICKED_TWICE, path: [index, "roleId"] });
    }
  });
});

const inclusionExclusionRuleSchema = z.object({
  type: z.string(),
  valueIds: z.array(z.string()),
});

const entityRulesSchema = z
  .record(
    z.string(),
    z.object({
      inclusions: z.array(inclusionExclusionRuleSchema),
      exclusions: z.array(inclusionExclusionRuleSchema),
    }),
  )
  .optional()
  .default({})
  .superRefine((rules, ctx) => {
    const r = rules ?? {};
    for (const [entityId, entry] of Object.entries(r)) {
      const incTypes = new Set(entry.inclusions.filter((x) => x.type).map((x) => x.type));
      const excTypes = new Set(entry.exclusions.filter((x) => x.type).map((x) => x.type));
      for (const t of incTypes) {
        if (excTypes.has(t)) {
          ctx.addIssue({
            code: "custom",
            message: "Conflicts with inclusion. Delete before adding as exclusion.",
            path: ["entityRules"],
          });
          return;
        }
      }

      entry.inclusions.forEach((rule, index) => {
        if (rule.type && rule.valueIds.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "At least one value is required",
            path: [entityId, "inclusions", index, "valueIds"],
          });
        }
      });

      entry.exclusions.forEach((rule, index) => {
        if (rule.type && rule.valueIds.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "At least one value is required",
            path: [entityId, "exclusions", index, "valueIds"],
          });
        }
      });
    }
  });

const addUserFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(PERSON_NAME_REGEX, PERSON_NAME_REGEX_MESSAGE),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(PERSON_NAME_REGEX, PERSON_NAME_REGEX_MESSAGE),
  email: z.string().trim().min(1, "Email is required").regex(emailRegex, "Enter a valid email"),
  phoneNumber: phoneNumberSchema,
  // Clearing the picker sends null; without its own message zod shows "expected string".
  reportsTo: z.string({ error: "Select a manager" }).min(1, "Select a manager"),
  assignedRoleIds: z.array(z.string()).min(1, "Select at least one role"),
  temporaryRoles: z.array(temporaryRoleSchema),
  newTemporaryRoles: newTemporaryRolesSchema,
  entityAccess: z.array(z.string()).min(1, "Select at least one entity"),
  entityRules: entityRulesSchema,
});

const superAdminFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(PERSON_NAME_REGEX, PERSON_NAME_REGEX_MESSAGE),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(PERSON_NAME_REGEX, PERSON_NAME_REGEX_MESSAGE),
  email: z.string().trim().min(1, "Email is required").regex(emailRegex, "Enter a valid email"),
  phoneNumber: phoneNumberSchema,
  reportsTo: z.string({ error: "Select a manager" }).min(1, "Select a manager"),
  assignedRoleIds: z.array(z.string()).min(1, "Select at least one role"),
  temporaryRoles: z.array(temporaryRoleSchema),
  newTemporaryRoles: newTemporaryRolesSchema,
  entityAccess: z.array(z.string()),
  entityRules: entityRulesSchema,
});

export type FormValues = z.input<typeof addUserFormSchema>;
type ParsedFormValues = z.infer<typeof addUserFormSchema>;

type AddUserStep = "details" | "entityAccess";

type CreateOutcome =
  | { kind: "idle" }
  | { kind: "failed"; refusal: string | null }
  | { kind: "partial"; userId: string; name: string; failures: FollowUpFailures };

/** The backend's refusals are written as sentences; a transport string ("status code 400") is not. */
const refusalReason = (body: unknown) => {
  const message = (body as { error?: { message?: unknown } } | undefined)?.error?.message;
  return typeof message === "string" && message.trim() ? message : CREATE_REFUSED_MESSAGE;
};

const isEmailTaken = (error: unknown) =>
  isAxiosError(error) &&
  error.response?.status === 409 &&
  error.response?.data?.error?.code === "UNIQUE_CONSTRAINT_VIOLATION";

/** Default user type for new users per Create User API contract. */
const CREATE_USER_DEFAULT_USER_TYPE = "servengine_user";

/** Build entityIds and entityRules from GET user tenantExclusions for form reset. */
function buildEntityAccessFromTenantExclusions(
  tenantExclusions: UserDetailTenantExclusion[] | undefined,
): {
  entityIds: string[];
  entityRules: Record<
    string,
    {
      inclusions: Array<{ type: string; valueIds: string[] }>;
      exclusions: Array<{ type: string; valueIds: string[] }>;
    }
  >;
} {
  const entityIds = Array.from(new Set((tenantExclusions ?? []).map((te) => te.entityId)));
  const entityRules: Record<
    string,
    {
      inclusions: Array<{ type: string; valueIds: string[] }>;
      exclusions: Array<{ type: string; valueIds: string[] }>;
    }
  > = {};

  for (const te of tenantExclusions ?? []) {
    const { entityId, categoryId, type, exclusionIds } = te;
    if (!entityRules[entityId]) {
      entityRules[entityId] = { inclusions: [], exclusions: [] };
    }
    const rule = { type: String(categoryId), valueIds: exclusionIds ?? [] };
    if (type === "inclusion") {
      const existing = entityRules[entityId].inclusions.find((r) => r.type === rule.type);
      if (existing) {
        existing.valueIds = [...new Set([...existing.valueIds, ...rule.valueIds])];
      } else {
        entityRules[entityId].inclusions.push(rule);
      }
    } else {
      const existing = entityRules[entityId].exclusions.find((r) => r.type === rule.type);
      if (existing) {
        existing.valueIds = [...new Set([...existing.valueIds, ...rule.valueIds])];
      } else {
        entityRules[entityId].exclusions.push(rule);
      }
    }
  }
  return { entityIds, entityRules };
}

/** Merge GET user temporary roles that share the same expiry into one form row. */
function groupTemporaryRolesByExpiry(
  temporaryRoles: UserDetailTemporaryRole[] | undefined,
): Array<{ roleIds: string[]; expiresAt: Date | null }> {
  if (!temporaryRoles?.length) return [];

  const groups = temporaryRoles.reduce((acc, tr) => {
    const expiresAt = tr.expiresAt ? new Date(tr.expiresAt) : null;
    const key = expiresAt && !Number.isNaN(expiresAt.getTime()) ? expiresAt.getTime() : -1;
    const bucket = acc.get(key);
    if (bucket) {
      bucket.roleIds.push(tr.id);
    } else {
      acc.set(key, { roleIds: [tr.id], expiresAt });
    }
    return acc;
  }, new Map<number, { roleIds: string[]; expiresAt: Date | null }>());

  return Array.from(groups.values()).sort((groupA, groupB) => {
    const groupAExpiresAtMs =
      groupA.expiresAt && !Number.isNaN(groupA.expiresAt.getTime())
        ? groupA.expiresAt.getTime()
        : 0;
    const groupBExpiresAtMs =
      groupB.expiresAt && !Number.isNaN(groupB.expiresAt.getTime())
        ? groupB.expiresAt.getTime()
        : 0;
    return groupAExpiresAtMs - groupBExpiresAtMs;
  });
}

/**
 * For updates: if an inclusion/exclusion row (entityId + categoryId + type) that
 * existed before is no longer present in the current form, send it with
 * isDeleted=true so the backend can clear it.
 *
 * - Removing one value from a MultiSelect (valueIds) = just send the updated
 *   exclusionIds in the payload; do NOT add isDeleted.
 * - Removing the entire inclusion/exclusion row (delete the row in the UI) =
 *   that row is no longer in the payload, so we add it with isDeleted: true.
 */
function addDeletedTenantExclusionsForRemovedEntities(
  payload: CreateUserPayload,
  originalTenantExclusions: UserDetailTenantExclusion[] | undefined,
  visibleEntityIds: string[] | null,
): CreateUserPayload {
  if (!originalTenantExclusions || originalTenantExclusions.length === 0) return payload;

  // Row identity: (entityId, categoryId, type) only — not exclusionIds.
  const rowKey = (te: { entityId: string; type: string; categoryId: number }) =>
    `${te.entityId}|${te.type}|${te.categoryId}`;

  const currentRowKeys = new Set(payload.tenantExclusions.map((te) => rowKey(te)));

  // Only a row the session could actually see can be "removed" by this form.
  // GET /users/:id returns every tenant the user belongs to, including entities
  // outside this session's scope, and those never render as cards here. Without
  // this guard an absent card reads as a deletion and the save silently clears
  // another entity's rules (SERV-2707).
  //
  // `null` means no filtering: the picker was rendered, so every entity the user
  // holds is on screen and an absent row really is a removal. Filtering is for
  // the scoped case alone, and the set is then the scoped entity itself. Keying
  // it on the current selection instead would be wrong in the other direction,
  // because unticking an entity is precisely how its rules get removed.
  const visibleEntityIdSet = visibleEntityIds === null ? null : new Set(visibleEntityIds);

  const deletedTenantExclusions: TenantExclusionItem[] = originalTenantExclusions
    .filter(
      (te) =>
        (visibleEntityIdSet === null || visibleEntityIdSet.has(te.entityId)) &&
        !currentRowKeys.has(rowKey(te)),
    )
    .map((te) => ({
      categoryId: te.categoryId,
      type: te.type,
      entityId: te.entityId,
      exclusionIds: te.exclusionIds ?? [],
      isDeleted: true,
    }));

  if (deletedTenantExclusions.length === 0) return payload;

  return {
    ...payload,
    tenantExclusions: [...payload.tenantExclusions, ...deletedTenantExclusions],
  };
}

/**
 * Builds the Create User API payload from validated add-user form values.
 * tenantExclusions: categoryId = rule.type (category id); exclusionIds = MultiSelect values.
 * temporaryRoles: only entries with at least one roleId; expiresAt as ISO string.
 */
function buildCreateUserPayload(values: ParsedFormValues): CreateUserPayload {
  const entityIds = (values.entityAccess ?? []).filter(Boolean);
  const tenantExclusions: TenantExclusionItem[] = [];

  const entityRules = values.entityRules ?? {};
  for (const [entityId, entry] of Object.entries(entityRules)) {
    if (!entry) continue;

    for (const rule of entry.inclusions ?? []) {
      if (!rule.type) continue;
      const categoryId = Number(rule.type);
      if (Number.isNaN(categoryId)) continue;
      tenantExclusions.push({
        categoryId,
        type: "inclusion",
        entityId,
        exclusionIds: rule.valueIds ?? [],
      });
    }

    for (const rule of entry.exclusions ?? []) {
      if (!rule.type) continue;
      const categoryId = Number(rule.type);
      if (Number.isNaN(categoryId)) continue;
      tenantExclusions.push({
        categoryId,
        type: "exclusion",
        entityId,
        exclusionIds: rule.valueIds ?? [],
      });
    }
  }

  const temporaryRoles = (values.temporaryRoles ?? [])
    .filter((tr) => tr.roleIds.length > 0)
    .map((tr) => ({
      roleIds: tr.roleIds,
      expiresAt: tr.expiresAt ? tr.expiresAt.toISOString() : null,
    }));

  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    phoneNumber: (values.phoneNumber ?? "").trim(),
    userType: CREATE_USER_DEFAULT_USER_TYPE,
    reportsTo: values.reportsTo,
    assignedRoleIds: values.assignedRoleIds ?? [],
    entityIds,
    tenantExclusions,
    temporaryRoles,
  };
}

export interface UserFormPageProps {
  userId?: string;
}

export function UserFormPage({ userId }: UserFormPageProps) {
  const navigate = useNavigate();
  const { isMainAppLogin: isMainAppLoginSession, userType } = useActiveSession();
  // The backend homes a new super admin in the request's entity, so the create is offered only at
  // the ServEngine level: a ServEngine login with no entity picked or record opened.
  const requestScope = useRequestScope();
  const isSuperAdmin = userType === SUPER_ADMIN;
  const isEditMode = Boolean(userId);
  const canCreateSuperAdmin =
    !isEditMode && isSuperAdmin && isMainAppLoginSession && !isEntityScoped(requestScope);
  // UI26: the same place, where the role picker lists the platform company's roles; an entity
  // role is then given as each picked entity's own copy.
  const atServEngineLevel = canCreateSuperAdmin;
  const {
    data: entityAccessOptionsFromQuery,
    total: entityAccessTotal,
    isLoading: isEntityAccessLoading,
    fetchNextPage: fetchNextEntityAccessPage,
    hasNextPage: hasMoreEntityAccess,
    isFetchingNextPage: isLoadingMoreEntityAccess,
  } = useEntityAccessOptions();
  const { data: userData, isLoading: isUserDataLoading } = useUserById(userId);

  const entityAccessOptions: DropDownOption[] = entityAccessOptionsFromQuery ?? [];

  // One row back from the entity dropdown means this session can only ever grant
  // that entity, so there is no choice to present. Read off the server's own
  // scoped response rather than re-deriving scope from session flags: the
  // backend already narrows it (`userManagement/repository.ts`, `case "entity"`),
  // and a picker returns on its own if multi-entity granting is ever opened up.
  const scopedEntityId =
    !isEntityAccessLoading && entityAccessTotal === 1
      ? (entityAccessOptions[0]?.value ?? null)
      : null;

  const [currentStep, setCurrentStep] = useState<AddUserStep>("details");

  const validationTriggeredRef = useRef(false);

  // Editing keeps the login's form; creating follows the pick, since Super Admin is chosen from the
  // roles list and has no entity step to validate.
  const methods = useForm<FormValues>({
    resolver: (values, context, options) =>
      zodResolver(
        (
          isEditMode
            ? isMainAppLoginSession
            : (values.assignedRoleIds ?? []).includes(SUPER_ADMIN_ROLE_ID)
        )
          ? superAdminFormSchema
          : addUserFormSchema,
      )(values, context, options as never),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      reportsTo: "",
      assignedRoleIds: [],
      temporaryRoles: [],
      newTemporaryRoles: [],
      entityAccess: [],
      entityRules: {},
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    trigger,
    reset,
    setValue,
    setError,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = methods;
  const watchedEntityAccess = watch("entityAccess");
  const watchedAssignedRoleIds = watch("assignedRoleIds");
  const isMainAppLogin = isEditMode
    ? isMainAppLoginSession
    : (watchedAssignedRoleIds ?? []).includes(SUPER_ADMIN_ROLE_ID);
  const watchedNewTemporaryRoles = watch("newTemporaryRoles");

  const { mutateAsync: createNewUser, isPending: isCreatingUser } = useCreateUser();
  const { mutateAsync: retryNewUserFollowUps, isPending: isRetrying } = useRetryNewUserFollowUps();
  const isCreating = isCreatingUser || isRetrying;
  const [createOutcome, setCreateOutcome] = useState<CreateOutcome>({ kind: "idle" });
  const takenEmailRef = useRef<string | null>(null);

  const roleCopies = useServEngineRoleCopies(watchedEntityAccess ?? [], atServEngineLevel);
  const scope = useNewUserScope({
    enabled: !isEditMode,
    entityIds: watchedEntityAccess ?? [],
    entityOptions: entityAccessOptions,
    assignedRoleIds: watchedAssignedRoleIds ?? [],
    temporaryRoleIds: (watchedNewTemporaryRoles ?? []).map((row) => row.roleId),
  });

  const markEmailTaken = (shouldFocus: boolean) =>
    setError("email", { type: "inUse", message: EMAIL_IN_USE }, { shouldFocus });

  const handleEmailBlur = async (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (isEditMode || !emailRegex.test(normalized)) return;
    try {
      const inUse = await emailInUseClient.isEmailInUse(normalized);
      if (inUse === null) return;
      // A late answer for an email the field no longer holds changes nothing, so an older "free"
      // cannot clear a newer "taken".
      if (getValues("email").trim().toLowerCase() !== normalized) return;
      takenEmailRef.current = inUse ? normalized : null;
      if (inUse) markEmailTaken(false);
    } catch {
      // An unanswered check must never block anyone: the create call refuses a taken email too.
    }
  };

  const { mutate: createSuperAdminMutation, isPending: isSuperAdminCreatePending } =
    useCreateSuperAdmin();
  const { mutate: updateUserMutation, isPending: isUpdatePending } = useUpdateUser();
  const { mutate: updateSuperAdminMutation, isPending: isSuperAdminUpdatePending } =
    useUpdateSuperAdmin();
  const isPending = isEditMode
    ? isMainAppLogin
      ? isSuperAdminUpdatePending
      : isUpdatePending
    : isMainAppLogin
      ? isSuperAdminCreatePending
      : isCreating;
  const [closeConfirmOpened, { open: openCloseConfirm, close: closeCloseConfirm }] =
    useDisclosure(false);

  const originalAssignedRoleIdsRef = useRef<string[]>([]);
  const originalTemporaryRoleIdsRef = useRef<string[]>([]);

  // When editing, populate form from GET /user-management/users/:userId
  useEffect(() => {
    if (!userData?.data) return;
    const user = userData.data;
    const { entityRules } = buildEntityAccessFromTenantExclusions(user.tenantExclusions);
    reset({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      phoneNumber: user.phoneNumber ?? "",
      reportsTo: user.reportsTo?.id ?? "",
      // TODO(SERV-3423): Edit at ServEngine level still assumes everyone edited is a Super Admin, which
      // only held while New User there made nothing else. Decide per person with
      // GET /access/users/:id/effective?login=servengine (`superUser`) — stage 13/14, the user page.
      // Saving this form changes only name, phone and manager (verified on dev 2026-10-28).
      assignedRoleIds: isMainAppLoginSession
        ? [SUPER_ADMIN_ROLE_ID]
        : (user.assignedRoles ?? []).map((r) => r.id),
      temporaryRoles: groupTemporaryRolesByExpiry(user.temporaryRoles).map((group) => ({
        roleIds: group.roleIds,
        expiresAt: group.expiresAt,
      })),
      newTemporaryRoles: [],
      entityAccess: user.entityIds ?? [],
      entityRules,
    });

    originalAssignedRoleIdsRef.current = (user.assignedRoles ?? []).map(
      (r: UserDetailAssignedRole) => r.id,
    );
    originalTemporaryRoleIdsRef.current = (user.temporaryRoles ?? []).map(
      (tr: UserDetailTemporaryRole) => tr.id,
    );
  }, [userData, reset]);

  // With the picker hidden, the entity the invite lands in comes from the
  // server's scoped dropdown instead of a control the user has to tick. Pinning
  // it keeps the inclusion/exclusion cards and the submitted tenantExclusions
  // keyed to that entity. Runs after the edit-mode reset too, which is why it
  // watches the field rather than firing once (SERV-2707).
  useEffect(() => {
    if (!scopedEntityId) return;
    const current = watchedEntityAccess ?? [];
    if (current.length === 1 && current[0] === scopedEntityId) return;
    setValue("entityAccess", [scopedEntityId], { shouldValidate: false });
  }, [scopedEntityId, watchedEntityAccess, setValue]);

  const handleClose = () => {
    navigate({ to: "/user-management" });
  };

  const handleCloseClick = () => {
    if (isDirty) {
      openCloseConfirm();
    } else {
      handleClose();
    }
  };

  const handleConfirmLeave = () => {
    closeCloseConfirm();
    handleClose();
  };

  // Both step-1 buttons run trigger(), which clears the manual "in use" error, so each re-marks
  // an email the blur check found taken instead of letting it through.
  const refuseTakenEmail = (): boolean => {
    if (isEditMode || takenEmailRef.current !== getValues("email").trim().toLowerCase()) {
      return false;
    }
    markEmailTaken(true);
    return true;
  };

  const handleNextStep = async () => {
    validationTriggeredRef.current = true;
    const isValid = await trigger([
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "reportsTo",
      "assignedRoleIds",
      "temporaryRoles",
      "newTemporaryRoles",
    ]);

    if (refuseTakenEmail()) return;

    if (isValid) {
      setCurrentStep("entityAccess");
    }
  };

  const handleSuperAdminSubmit = async () => {
    validationTriggeredRef.current = true;
    const isValid = await trigger([
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "reportsTo",
      "assignedRoleIds",
    ]);

    if (refuseTakenEmail()) return;

    if (isValid) {
      void handleSubmit(onSuperAdminSubmit)();
    }
  };

  const handleBackToDetails = () => {
    setCurrentStep("details");
  };

  const onSuperAdminSubmit = (values: FormValues) => {
    if (isEditMode && userId) {
      const updatePayload: UpdateSuperAdminPayload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phoneNumber: (values.phoneNumber ?? "").trim(),
        reportsTo: values.reportsTo,
      };
      updateSuperAdminMutation(
        { userId, payload: updatePayload },
        {
          onSuccess: (response) => {
            handleApiSuccess(response.message ?? "Super admin updated successfully");
            navigate({ to: "/user-management" });
          },
          onError: (error) => {
            handleApiError(error as ApiError);
          },
        },
      );
      return;
    }

    const createPayload: CreateSuperAdminPayload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phoneNumber: (values.phoneNumber ?? "").trim(),
      email: values.email.trim(),
      reportsTo: values.reportsTo,
    };
    createSuperAdminMutation(createPayload, {
      onSuccess: () => {
        handleApiSuccess(createdToast(`${createPayload.firstName} ${createPayload.lastName}`));
        navigate({ to: "/user-management" });
      },
      onError: (error) => {
        if (isEmailTaken(error)) {
          takenEmailRef.current = createPayload.email.toLowerCase();
          markEmailTaken(true);
          return;
        }
        handleApiError(error as ApiError);
      },
    });
  };

  const submitNewUser = async (parsedData: ParsedFormValues) => {
    if (!scope.checkBeforeCreate()) return;

    const entityNameOf = (entityId: string) =>
      scope.companies.find((company) => company.tenantId === entityId)?.tenantName ?? entityId;
    if (atServEngineLevel && !roleCopies.isReady) {
      setCreateOutcome({ kind: "failed", refusal: ROLE_COPIES_UNREAD });
      return;
    }
    const servEngineRoles = atServEngineLevel
      ? planServEngineRoles(
          parsedData.assignedRoleIds,
          parsedData.newTemporaryRoles.map((row) => ({
            roleId: row.roleId,
            endsOn: row.endsOn ?? "",
          })),
          roleCopies.resolve,
        )
      : null;
    if (servEngineRoles && servEngineRoles.missing.length > 0) {
      setCreateOutcome({
        kind: "failed",
        refusal: noEntityCopyMessage(
          servEngineRoles.missing.map(({ roleId, entityId }) => ({
            roleName: scope.roleNameOf(roleId),
            entityName: entityNameOf(entityId),
          })),
        ),
      });
      return;
    }

    const temporaryRoles: NamedTemporaryRole[] = servEngineRoles
      ? servEngineRoles.temporaryRoles.map((row) => ({
          roleId: row.roleId,
          endsOn: row.endsOn,
          roleName: row.entityId
            ? roleInEntity(scope.roleNameOf(row.pickedRoleId), entityNameOf(row.entityId))
            : scope.roleNameOf(row.pickedRoleId),
          entityId: row.entityId,
        }))
      : parsedData.newTemporaryRoles.map((row) => ({
          roleId: row.roleId,
          endsOn: row.endsOn ?? "",
          roleName: scope.roleNameOf(row.roleId),
        }));
    const entities = scope.companies.map((company) => ({
      company,
      writes: planNewUserWrites(company, scope.rulesByEntity[company.tenantId] ?? []),
    }));
    const name = `${parsedData.firstName.trim()} ${parsedData.lastName.trim()}`;

    setCreateOutcome({ kind: "idle" });
    try {
      const { userId: createdId, failures } = await createNewUser({
        payload: {
          ...buildCreateUserPayload(parsedData),
          ...(servEngineRoles ? { assignedRoleIds: servEngineRoles.assignedRoleIds } : {}),
        },
        temporaryRoles,
        entities,
      });
      if (nothingFailed(failures)) {
        handleApiSuccess(createdToast(name));
        navigate({ to: "/user-management" });
        return;
      }
      setCreateOutcome({ kind: "partial", userId: createdId, name, failures });
    } catch (error) {
      if (isEmailTaken(error)) {
        takenEmailRef.current = parsedData.email.trim().toLowerCase();
        setCurrentStep("details");
        setTimeout(() => markEmailTaken(true));
        return;
      }
      // An answer means the server refused and said why; no answer means it may have gone through.
      setCreateOutcome({
        kind: "failed",
        refusal: isAxiosError(error) && error.response ? refusalReason(error.response.data) : null,
      });
    }
  };

  const retryFollowUps = async () => {
    if (createOutcome.kind !== "partial") return;
    const failures = await retryNewUserFollowUps({
      userId: createOutcome.userId,
      failures: createOutcome.failures,
    });
    if (nothingFailed(failures)) {
      handleApiSuccess(createdToast(createOutcome.name));
      navigate({ to: "/user-management" });
      return;
    }
    setCreateOutcome({ ...createOutcome, failures });
  };

  const onSubmit = (values: FormValues) => {
    const parsedData: ParsedFormValues = addUserFormSchema.parse(values);
    if (!isEditMode) {
      void submitNewUser(parsedData);
      return;
    }
    let createPayload: CreateUserPayload = buildCreateUserPayload(parsedData);
    let payload: CreateUserPayload | UpdateUserPayload = createPayload;

    if (isEditMode && userData?.data) {
      // Append deleted tenantExclusions for entities that were removed
      if (userData.data.tenantExclusions) {
        createPayload = addDeletedTenantExclusionsForRemovedEntities(
          createPayload,
          userData.data.tenantExclusions,
          // Not the dropdown's options array: it is padded with a "Loading" or
          // "No data found" row, so a failed or empty entity fetch would yield a
          // set of {"no-data"} and silently swallow every real deletion.
          scopedEntityId ? [scopedEntityId] : null,
        );
      }

      const currentAssignedRoleIds = (parsedData.assignedRoleIds ?? []).filter(Boolean);
      const originalAssignedRoleIds = originalAssignedRoleIdsRef.current;

      const addedRoleIds = currentAssignedRoleIds.filter(
        (id) => !originalAssignedRoleIds.includes(id),
      );
      const removedRoleIds = originalAssignedRoleIds.filter(
        (id) => !currentAssignedRoleIds.includes(id),
      );
      const baseAssignedRoleIds = originalAssignedRoleIds.filter(
        (id) => !removedRoleIds.includes(id),
      );

      const originalTemporaryRoleIds = originalTemporaryRoleIdsRef.current;
      const originalTemporaryRoleIdSet = new Set(originalTemporaryRoleIds);
      const currentTemporaryRoles = (parsedData.temporaryRoles ?? []).filter(
        (tr) => tr.roleIds.length > 0,
      );
      const currentTemporaryRoleIdSet = new Set(currentTemporaryRoles.flatMap((tr) => tr.roleIds));

      const removedTemporaryRoles = originalTemporaryRoleIds.filter(
        (id) => !currentTemporaryRoleIdSet.has(id),
      );

      const retainedTemporaryRoles = currentTemporaryRoles
        .map((tr) => ({
          roleIds: tr.roleIds.filter((id) => originalTemporaryRoleIdSet.has(id)),
          expiresAt: tr.expiresAt,
        }))
        .filter((tr) => tr.roleIds.length > 0);

      const addedTemporaryRoles = currentTemporaryRoles
        .map((tr) => ({
          roleIds: tr.roleIds.filter((id) => !originalTemporaryRoleIdSet.has(id)),
          expiresAt: tr.expiresAt,
        }))
        .filter((tr) => tr.roleIds.length > 0);

      const { entityIds: _entityIds, ...payloadWithoutEntityIds } = createPayload;

      const updatePayload: UpdateUserPayload = {
        // entityIds is omitted only when the picker was hidden. The backend
        // treats a present list as authoritative and soft-deletes every
        // membership absent from it (`userManagement/repository.ts`, "Handle
        // entityid changes"), so a form with no picker would revoke access to
        // entities it cannot even show. When the picker is real the user's
        // selection IS the intended access, and dropping it would make ticking
        // an entity do nothing (SERV-2707).
        ...(scopedEntityId ? payloadWithoutEntityIds : createPayload),
        // For updates, assignedRoleIds/temporaryRoles should only include the
        // original roles that remain; newly added ones are sent via "added*" fields.
        assignedRoleIds: baseAssignedRoleIds,
        temporaryRoles: retainedTemporaryRoles.map((tr) => ({
          roleIds: tr.roleIds,
          expiresAt: tr.expiresAt ? tr.expiresAt.toISOString() : null,
        })),
        ...(addedRoleIds.length ? { addedRoleIds: Array.from(new Set(addedRoleIds)) } : {}),
        ...(removedRoleIds.length ? { removedRoleIds: Array.from(new Set(removedRoleIds)) } : {}),
        ...(addedTemporaryRoles.length
          ? {
              addedTemporaryRoles: addedTemporaryRoles.map((tr) => ({
                roleIds: tr.roleIds,
                expiresAt: tr.expiresAt ? tr.expiresAt.toISOString() : null,
              })),
            }
          : {}),
        ...(removedTemporaryRoles.length
          ? { removedTemporaryRoles: Array.from(new Set(removedTemporaryRoles)) }
          : {}),
      };

      payload = updatePayload;
    }

    if (isEditMode && userId) {
      updateUserMutation(
        { userId, payload: payload as UpdateUserPayload },
        {
          onSuccess: (response) => {
            handleApiSuccess(response.message ?? "User updated successfully");
            navigate({ to: "/user-management" });
          },
          onError: (error) => {
            handleApiError(error as ApiError);
          },
        },
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <Box className={styles.container}>
        <Header title={isEditMode ? "Edit User" : "Add New User"} onClose={handleCloseClick} />
        <Box className={styles.body}>
          <Box className={styles.formWrapper}>
            {isEditMode && isUserDataLoading ? (
              <Loader size="medium" />
            ) : (
              <Stack gap="md">
                {currentStep === "details" && (
                  <Stack gap="md">
                    <DetailsStepSection
                      validationTriggeredRef={validationTriggeredRef}
                      isEditMode={isEditMode}
                      excludeUserId={userId}
                      initialAssignedRoles={
                        userData?.data?.assignedRoles?.map((r) => ({
                          value: r.id,
                          label: r.name,
                        })) ?? []
                      }
                      initialTemporaryRoles={
                        userData?.data?.temporaryRoles?.map((r) => ({
                          value: r.id,
                          label: r.name,
                        })) ?? []
                      }
                      initialReportsTo={
                        userData?.data?.reportsTo
                          ? {
                              value: userData.data.reportsTo.id,
                              label: userData.data.reportsTo.name,
                            }
                          : null
                      }
                      isMainAppLogin={!!isMainAppLogin}
                      onEmailBlur={(email) => void handleEmailBlur(email)}
                      canCreateSuperAdmin={canCreateSuperAdmin}
                    />
                    <Group className={styles.actionsRow}>
                      {isMainAppLogin ? (
                        <BrandButton
                          variant="primary"
                          onClick={handleSuperAdminSubmit}
                          isDisabled={isPending}
                        >
                          {isEditMode ? "Submit" : CREATE_USER}
                        </BrandButton>
                      ) : (
                        <BrandButton
                          variant="primary"
                          onClick={handleNextStep}
                          isDisabled={isPending}
                        >
                          Next
                        </BrandButton>
                      )}
                    </Group>
                  </Stack>
                )}

                {!isMainAppLogin && !isEditMode && currentStep === "entityAccess" && (
                  <Stack gap="md">
                    {createOutcome.kind === "partial" ? (
                      <Alert
                        type="danger"
                        fullWidth
                        title={partlySavedTitle(createOutcome.name)}
                        message={partlySavedMessage(
                          createOutcome.failures.temporaryRoles.map((role) => role.roleName),
                          createOutcome.failures.entities.map(
                            (entity) => entity.company.tenantName,
                          ),
                        )}
                      />
                    ) : createOutcome.kind === "failed" ? (
                      <Alert
                        type="danger"
                        fullWidth
                        title={createOutcome.refusal ? CREATE_REFUSED_TITLE : CREATE_FAILED_TITLE}
                        message={createOutcome.refusal ?? CREATE_FAILED_MESSAGE}
                      />
                    ) : scope.unreadRoleCount > 0 ? (
                      <Alert
                        type="warning"
                        fullWidth
                        title={ROLES_UNREAD_TITLE}
                        message={rolesUnreadMessage(scope.unreadRoleCount)}
                      />
                    ) : null}
                    {/* After a partial save the person exists and Try Again resends only what
                        failed, so the rows are not left editable as if a change would count. */}
                    {createOutcome.kind !== "partial" && (
                      <NewUserEntityAccessStep
                        entityAccessOptions={entityAccessOptions}
                        scopedEntityId={scopedEntityId}
                        isLoading={isEntityAccessLoading}
                        hasMoreEntityAccess={hasMoreEntityAccess}
                        isLoadingMoreEntityAccess={isLoadingMoreEntityAccess}
                        onLoadMoreEntityAccess={() => {
                          void fetchNextEntityAccessPage();
                        }}
                        companies={scope.companies}
                        rulesByEntity={scope.rulesByEntity}
                        onRulesChange={scope.setEntityRules}
                        showErrors={scope.showErrors}
                        requiredError={scope.requiredError}
                        attempt={scope.blockedAttempt}
                        blockedTenantIds={scope.blockedTenantIds}
                      />
                    )}
                    <Group className={styles.actionsRow}>
                      {createOutcome.kind === "partial" ? (
                        <>
                          <BrandButton
                            variant="tertiary"
                            onClick={handleClose}
                            isDisabled={isCreating}
                          >
                            {DONE}
                          </BrandButton>
                          <BrandButton
                            variant="primary"
                            onClick={() => void retryFollowUps()}
                            isDisabled={isCreating}
                          >
                            {isCreating ? CREATING : TRY_AGAIN}
                          </BrandButton>
                        </>
                      ) : (
                        <>
                          <BrandButton
                            variant="tertiary"
                            onClick={handleBackToDetails}
                            isDisabled={isCreating}
                          >
                            Back
                          </BrandButton>
                          <BrandButton
                            variant="primary"
                            onClick={handleSubmit(onSubmit)}
                            isDisabled={
                              isCreating ||
                              scope.rolesStillLoading ||
                              (scope.showErrors && scope.blockedTenantIds.size > 0)
                            }
                          >
                            {isCreating ? CREATING : CREATE_USER}
                          </BrandButton>
                        </>
                      )}
                    </Group>
                  </Stack>
                )}

                {!isMainAppLogin && isEditMode && currentStep === "entityAccess" && (
                  <Stack gap="md">
                    <EntityAccessStepSection
                      entityAccessOptions={entityAccessOptions}
                      scopedEntityId={scopedEntityId}
                      isLoading={isEntityAccessLoading}
                      hasMoreEntityAccess={hasMoreEntityAccess}
                      isLoadingMoreEntityAccess={isLoadingMoreEntityAccess}
                      onLoadMoreEntityAccess={() => {
                        void fetchNextEntityAccessPage();
                      }}
                    />
                    {(errors.entityRules as { message?: string } | undefined)?.message && (
                      <Text size="sm" c="var(--text-danger)" className={styles.entityRulesError}>
                        {(errors.entityRules as { message: string }).message}
                      </Text>
                    )}
                    <Group className={styles.actionsRow}>
                      <BrandButton
                        variant="tertiary"
                        onClick={handleBackToDetails}
                        isDisabled={isPending}
                      >
                        Back
                      </BrandButton>
                      <BrandButton
                        variant="primary"
                        onClick={handleSubmit(onSubmit)}
                        isDisabled={isPending}
                      >
                        Update User
                      </BrandButton>
                    </Group>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      <ConfirmationModal
        isOpen={closeConfirmOpened}
        onClose={closeCloseConfirm}
        title="Unsaved changes"
        body="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        confirmText="Leave"
        cancelText="Cancel"
        onConfirm={handleConfirmLeave}
      />
    </FormProvider>
  );
}

function AddUserPage() {
  return <UserFormPage />;
}
